import {makeRepositoryReference} from './copilot-chat-helpers'
import type {CopilotChatState} from './copilot-chat-reducer-types'
import {constructMessagesHierarchy} from './copilot-chat-subthreading-helpers'
import type {
  CopilotChatMessage,
  CopilotChatMode,
  CopilotChatOrg,
  CopilotChatPayload,
  CopilotChatReference,
  CopilotChatRepo,
  CopilotChatThread,
  CopilotSpace,
  RepositoryReference,
} from './copilot-chat-types'
import {copilotLocalStorage} from './copilot-local-storage'
import {getDefaultModel} from './models'

const emptySet: ReadonlySet<CopilotChatReference> = new Set<CopilotChatReference>()

export interface BuildCopilotChatInitialStateOptions {
  topic?: CopilotChatRepo
  threadId: string | null
  workerPath: string
  refs: CopilotChatReference[]
  selectedReference?: CopilotChatReference | null
  mode: CopilotChatMode
  ssoOrganizations: CopilotChatOrg[]
  chatIsOpen?: boolean
  chatIsVisible?: boolean
  chatVisibleSettingPath?: string
  customCopilots?: CopilotSpace[]
  messages?: CopilotChatMessage[]
  testReducerState?: CopilotChatState
  currentView?: 'thread' | 'list'
  copilotChatPayload: CopilotChatPayload
  restoredMessages?: readonly CopilotChatMessage[]
  restoredThreadTitle?: string | null
}

export function buildCopilotChatInitialState({
  topic,
  threadId,
  workerPath,
  refs,
  selectedReference,
  mode,
  ssoOrganizations,
  chatIsOpen,
  chatIsVisible,
  chatVisibleSettingPath,
  customCopilots,
  messages,
  testReducerState,
  currentView,
  copilotChatPayload,
  restoredMessages,
  restoredThreadTitle,
}: BuildCopilotChatInitialStateOptions): CopilotChatState {
  if (testReducerState) return testReducerState

  const hierarchicalRestoredMessages = restoredMessages ? constructMessagesHierarchy(restoredMessages) : undefined
  const topicAsReference: RepositoryReference | undefined = topic ? makeRepositoryReference(topic) : undefined
  const defaultModel = getDefaultModel(null, null)

  return {
    threadSelectedAt: new Date(),
    threadsLoading: {state: 'pending', error: null},
    messagesLoading: {state: 'pending', error: null},
    messagesRestored: !!hierarchicalRestoredMessages,
    slashCommandLoading: {state: 'pending', error: null},
    showTopicPicker: mode !== 'immersive' && (!threadId || mode === 'assistive') && !topic,
    topicLoading: {state: 'pending', error: null},
    threads: new Map<string, CopilotChatThread>(),
    restoredThreadTitle,
    model: defaultModel,
    availableModels: [defaultModel],
    modelsLoading: {state: 'pending', error: null},
    messages: messages ?? hierarchicalRestoredMessages ?? [],
    streamingMessage: null,
    selectedThreadID: threadId,
    currentTopic: undefined,
    chatIsOpen: Boolean(chatIsOpen),
    isWaitingOnCopilot: false,
    isWaitingOnAttachment: false,
    attachmentProcessingType: null,
    currentUserLogin: copilotChatPayload.currentUserLogin,
    apiUrl: copilotChatPayload.apiURL,
    currentReferences: topicAsReference ? [topicAsReference, ...refs] : refs,
    implicitReferences: emptySet,
    findFileWorkerPath: workerPath,
    currentView: currentView || 'thread',
    selectedReference: selectedReference ?? null,
    mode,
    currentRepository: topic,
    ssoOrganizations,
    context: undefined,
    customInstructions: copilotChatPayload.customInstructions,
    chatIsVisible,
    chatVisibleSettingPath,
    renderBetaLabel: copilotChatPayload.renderBetaLabel,
    topRepositoriesCache: undefined,
    customCopilotsEnabled: copilotChatPayload.customCopilotsEnabled ?? false,
    customCopilotsIndividualAccessEnabled: copilotChatPayload.customCopilotsIndividualAccessEnabled ?? true,
    copilotChatSettingEnabled: copilotChatPayload.copilotChatSettingEnabled ?? false,
    optedInToPreviewFeatures: copilotChatPayload.optedInToPreviewFeatures,
    optedInToUserFeedback: copilotChatPayload.optedInToUserFeedback,
    customCopilots,
    reviewLab: copilotChatPayload.reviewLab,
    repoCustomInstructionsEnabled: copilotLocalStorage.getRepoCustomInstructionsState(),
    ambientError: undefined,
    personalInstructions: copilotChatPayload.personalInstructions ?? null,
    threadHasNewMessages: false,
    sharedThreadsLoading: {state: 'pending', error: null},
    skillOptions: {
      deepCodeSearch: false,
    },
    wrapCodeLines: copilotLocalStorage.getWrapCodeLines(),
    showCopilotSpacesMcpBanner: copilotChatPayload.showCopilotSpacesMcpBanner ?? false,
    sharedThreadChannel: copilotChatPayload.sharedThreadChannel,
    autoSubmit: copilotChatPayload.autoSubmit ?? false,
    plan: copilotChatPayload.plan,
    agentQuotaSnapshot: copilotChatPayload.agentQuotaSnapshot,
    hasCEorCBAccess: copilotChatPayload.hasCEorCBAccess,
    agentsEnabled: copilotChatPayload.agentsEnabled,
    showDecoupledPRModeBanner: copilotChatPayload.showDecoupledPRModeBanner,
  }
}
